const POLL_INTERVAL_MS = 800;
const STABLE_CHECKS = 3;
const MAX_STABILIZATION_MS = 20_000;

type GetScrollSize = () => Promise<{ width: number; height: number }>;

type HeightManager = {
  startStabilization: () => void;
  attachResizeListeners: () => void;
  dispose: () => void;
};

// Manages iframe height after the Superset dashboard has initialised:
//
// 1. Stabilization — polls getScrollSize() until the height stops changing
//    (charts load asynchronously after the app shell renders).
// 2. Resize sync — re-queries the scroll size on window/container resize.
//
// Observes only mountPoint.parentElement, not mountPoint itself, to avoid
// an infinite loop where setting style.height triggers the observer again.
export function createHeightManager(getScrollSize: GetScrollSize, mountPoint: HTMLDivElement): HeightManager {
  let disposed = false;
  let stabilizationTimeout: ReturnType<typeof setTimeout> | null = null;
  let rafId: number | null = null;
  let resizeObserver: ResizeObserver | null = null;

  const applyHeight = (height: number) => {
    mountPoint.style.height = `${height}px`;
  };

  const syncHeight = async () => {
    if (disposed) return;
    try {
      const size = await getScrollSize();
      if (!disposed && size?.height > 0) applyHeight(size.height);
    } catch {
      // height stays as-is on transient error
    }
  };

  const onResize = () => {
    if (rafId) cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(() => void syncHeight());
  };

  const startStabilization = () => {
    let stableCount = 0;
    let lastHeight = 0;
    const startedAt = Date.now();

    const poll = async () => {
      if (disposed || Date.now() - startedAt >= MAX_STABILIZATION_MS) return;

      try {
        const size = await getScrollSize();
        if (disposed) return;

        if (size?.height > 0) {
          if (size.height !== lastHeight) {
            applyHeight(size.height);
            lastHeight = size.height;
            stableCount = 0;
          } else {
            stableCount += 1;
          }
        }
      } catch {
        // ignore transient errors, keep polling
      }

      if (stableCount < STABLE_CHECKS) {
        stabilizationTimeout = setTimeout(poll, POLL_INTERVAL_MS);
      }
    };

    stabilizationTimeout = setTimeout(poll, POLL_INTERVAL_MS);
  };

  const attachResizeListeners = () => {
    window.addEventListener('resize', onResize);

    if (typeof ResizeObserver !== 'undefined' && mountPoint.parentElement) {
      resizeObserver = new ResizeObserver(onResize);
      resizeObserver.observe(mountPoint.parentElement);
    }
  };

  const dispose = () => {
    disposed = true;
    if (rafId) cancelAnimationFrame(rafId);
    if (stabilizationTimeout) clearTimeout(stabilizationTimeout);
    window.removeEventListener('resize', onResize);
    resizeObserver?.disconnect();
  };

  return { startStabilization, attachResizeListeners, dispose };
}
